/**
 * Browser detection utilities for handling in-app browser compatibility issues
 * with Google OAuth and other authentication providers
 */

export interface BrowserInfo {
  isInAppBrowser: boolean;
  appName: string | null;
  platform: 'ios' | 'android' | 'desktop' | 'unknown';
  isMobile: boolean;
  userAgent: string;
}

/**
 * Common in-app browser identifiers
 */
const IN_APP_BROWSER_PATTERNS = {
  // Social media apps
  linkedin: /LinkedInApp/i,
  facebook: /FBAN|FBAV|Instagram|FBSV/i,
  twitter: /Twitter/i,
  tiktok: /TikTok/i,
  wechat: /MicroMessenger/i,
  line: /Line/i,
  whatsapp: /WhatsApp/i,
  telegram: /TelegramWebApp/i,
  discord: /Discord/i,

  // E-commerce and other apps
  amazon: /AmazonWebApp/i,
  ebay: /eBayApp/i,
  shopify: /ShopifyApp/i,

  // News and content apps
  reddit: /RedditApp/i,
  pinterest: /Pinterest/i,
  snapchat: /Snapchat/i,

  // Generic patterns for in-app browsers
  webview: /wv|WebView/i,
  inApp: /InAppBrowser|InApp/i,

  // Chinese apps
  weibo: /Weibo/i,
  qq: /QQ/i,
  alipay: /AlipayClient/i,
  taobao: /Taobao/i,

  // Korean apps
  naver: /Naver/i,
  kakao: /KakaoTalk/i,

  // Japanese apps
  yahoo: /YahooApp/i,
  mixi: /MixiApp/i,
} as const;

/**
 * Detect if the current browser is an in-app browser
 */
export function detectInAppBrowser(): BrowserInfo {
  if (typeof window === 'undefined') {
    return {
      isInAppBrowser: false,
      appName: null,
      platform: 'unknown',
      isMobile: false,
      userAgent: '',
    };
  }

  const userAgent = window.navigator.userAgent;
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(userAgent);

  // Determine platform
  let platform: BrowserInfo['platform'] = 'unknown';
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    platform = 'ios';
  } else if (/Android/i.test(userAgent)) {
    platform = 'android';
  } else if (!isMobile) {
    platform = 'desktop';
  }

  // Check for in-app browser patterns
  let appName: string | null = null;
  let isInAppBrowser = false;

  for (const [app, pattern] of Object.entries(IN_APP_BROWSER_PATTERNS)) {
    if (pattern.test(userAgent)) {
      isInAppBrowser = true;
      appName = app;
      break;
    }
  }

  // Additional checks for common in-app browser indicators
  if (!isInAppBrowser) {
    // Check for standalone mode (PWA) - iOS specific
    if (
      'standalone' in window.navigator &&
      (window.navigator as Navigator & { standalone?: boolean }).standalone
    ) {
      isInAppBrowser = true;
      appName = 'pwa';
    }

    // Check for specific WebView indicators
    if (/wv|WebView/i.test(userAgent)) {
      isInAppBrowser = true;
      appName = appName || 'webview';
    }
  }

  return {
    isInAppBrowser,
    appName,
    platform,
    isMobile,
    userAgent,
  };
}

/**
 * Check if the current browser is likely to have issues with Google OAuth
 */
export function hasGoogleOAuthIssues(): boolean {
  const browserInfo = detectInAppBrowser();

  // In-app browsers on mobile are most likely to have issues
  return browserInfo.isInAppBrowser && browserInfo.isMobile;
}

/**
 * Attempt to redirect to external browser for better OAuth compatibility
 */
export function redirectToExternalBrowser(url: string): boolean {
  const browserInfo = detectInAppBrowser();

  if (!browserInfo.isInAppBrowser || !browserInfo.isMobile) {
    return false;
  }

  try {
    // iOS Safari redirect (iOS 17+)
    if (browserInfo.platform === 'ios') {
      const safariUrl = `x-safari-${url}`;
      window.location.href = safariUrl;
      return true;
    }

    // Android Chrome redirect
    if (browserInfo.platform === 'android') {
      const chromeUrl = `googlechrome://navigate?url=${encodeURIComponent(url)}`;
      window.location.href = chromeUrl;

      // Fallback to regular URL if Chrome redirect fails
      setTimeout(() => {
        window.location.href = url;
      }, 1000);
      return true;
    }

    // Generic external browser redirect
    window.location.href = url;
    return true;
  } catch (error) {
    console.warn('Failed to redirect to external browser:', error);
    return false;
  }
}

/**
 * Show a warning message to users about in-app browser limitations
 */
export function showInAppBrowserWarning(): void {
  const browserInfo = detectInAppBrowser();

  if (!browserInfo.isInAppBrowser) {
    return;
  }

  const appName = browserInfo.appName || 'this app';
  const message = `For the best experience with Google Sign-In, please open this page in your device's default browser instead of ${appName}.`;

  // You can customize this to show a toast, modal, or other UI element
  console.warn(message);

  // Optionally show an alert (for development/testing)
  if (process.env.NODE_ENV === 'development') {
    alert(message);
  }
}

/**
 * Get a user-friendly app name for display
 */
export function getAppDisplayName(appName: string | null): string {
  if (!appName) return 'this app';

  const displayNames: Record<string, string> = {
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    wechat: 'WeChat',
    line: 'LINE',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    discord: 'Discord',
    amazon: 'Amazon',
    ebay: 'eBay',
    shopify: 'Shopify',
    reddit: 'Reddit',
    pinterest: 'Pinterest',
    snapchat: 'Snapchat',
    weibo: 'Weibo',
    qq: 'QQ',
    alipay: 'Alipay',
    taobao: 'Taobao',
    naver: 'Naver',
    kakao: 'KakaoTalk',
    yahoo: 'Yahoo',
    mixi: 'Mixi',
    pwa: 'this app',
    webview: 'this app',
  };

  return displayNames[appName] || appName;
}
